"use client"

import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { useMemo } from "react"

export interface DatePickerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value?: Date
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  buttonClassName?: string
  calendarProps?: Omit<
    React.ComponentProps<typeof Calendar>,
    "mode" | "selected" | "onSelect"
  >
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled,
  buttonClassName,
  calendarProps,
  className,
  ...props
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const formattedDate = useMemo(() => {
    try {
      return value ? format(value, "EEE, dd/MM") : undefined
    } catch {
      return undefined
    }
  }, [value])

  return (
    <div className={cn("inline-flex", className)} {...props}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !value && "text-muted-foreground",
              buttonClassName
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formattedDate || <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            {...calendarProps}
            mode="single"
            selected={value}
            onSelect={(date) => {
              onChange?.(date)
              setOpen(false)
            }}
            classNames={{
              ...calendarProps?.classNames,
              day: cn(calendarProps?.classNames?.day, "hover:bg-transparent"),
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
